import { Link } from '@redwoodjs/router'

import { EmploymentTypeLabels } from 'src/graphql/types/jobListings'
import {
  formatLicenseClasses,
  formatLocation,
  formatMinExperience,
  formatPay,
  ShortFormJobListing,
} from 'src/pages/ShortFormPage/jobInfo.utils'
import { cn } from 'src/utils'

/**
 * One card on the public job board — the `PublicJobCard` projection returned by
 * `publicJobBoard`. Deliberately a subset of `ShortFormJobListing` so the card
 * and the detail panel share one set of formatters.
 */
export interface PublicJobCardData
  extends Pick<
    ShortFormJobListing,
    | 'jobListingId'
    | 'title'
    | 'subtitle'
    | 'type'
    | 'payMin'
    | 'payMax'
    | 'payUnit'
    | 'payCurrency'
    | 'employmentType'
    | 'licenseClass'
    | 'minMonthsExperience'
    | 'address'
  > {
  /** The listing's ACTIVE COMPANY_WEBSITE tracking link. */
  trackingLink: string
}

export interface JobCardProps {
  card: PublicJobCardData
  /**
   * Falls back to a company-name chip when the listing has no location,
   * employment type, CDL class or experience to show — a card is never a bare
   * title and a button.
   */
  companyName?: string | null
  isSelected?: boolean
  /**
   * Where the body links to. A real href so the card can be middle-clicked,
   * Cmd-clicked, copied or previewed — and so assistive tech announces a link
   * for what is, on mobile, a page change.
   */
  selectHref: string
  /**
   * Mobile only. On desktop the detail card's own sticky bar is the single
   * commit control, and a second Apply on every rail card competed with it.
   * On mobile there is no detail pane beside the list, so Apply is what says
   * "start here" — it goes where the card goes, since a tap already opens the
   * job's page.
   */
  showApply?: boolean
  /** The body was activated — open the job at the top of the description. */
  onSelect: (card: PublicJobCardData) => void
}

const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center whitespace-nowrap rounded-full border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-[9px] py-[3px] text-[11px] font-semibold text-doubleNickel-gray-700">
    {children}
  </span>
)

/**
 * A position on the board.
 *
 * Two separate controls, never nested: the body (title, blurb, chips) selects
 * the job, and Apply — mobile only, see `showApply` — is a sibling button on
 * the pay row. Nesting the button inside the body's clickable region would
 * break keyboard and screen-reader navigation, so the pay row sits outside
 * the body control.
 */
const JobCard = ({
  card,
  companyName,
  isSelected = false,
  selectHref,
  showApply = false,
  onSelect,
}: JobCardProps) => {
  const location = formatLocation(card.address)
  const licenseClasses = formatLicenseClasses(card.licenseClass)
  const minExperience = formatMinExperience(card.minMonthsExperience)
  // Compact drops cents from whole-dollar amounts — "$1,450 – $1,800 / week",
  // not "$1,450.00 – $1,800.00 / week", which is too long for a card
  const pay = formatPay(card, { compact: true })

  // Max 4, in order: location, employment type, CDL class, min experience
  const chips = [
    location,
    card.employmentType ? EmploymentTypeLabels[card.employmentType] : null,
    licenseClasses,
    minExperience ? `${minExperience} exp.` : null,
  ]
    .filter((chip): chip is string => Boolean(chip))
    .slice(0, 4)

  // Worst case in real data: a title and nothing else. The company name keeps
  // the card from rendering as a bare title with a button.
  const displayChips =
    chips.length > 0
      ? chips
      : [companyName].filter((chip): chip is string => Boolean(chip))

  return (
    <article
      className={cn(
        'relative flex flex-col rounded-xl border border-doubleNickel-gray-200 bg-white p-4 text-left sm:p-5',
        'hover:border-doubleNickel-gray-300 hover:shadow',
        // Selected reads as a quiet fill, not a blue outline — the rail is a
        // list of twelve, and an accent ring on one of them shouted.
        isSelected && 'border-doubleNickel-gray-300 bg-doubleNickel-gray-50'
      )}
    >
      {/* A real link, not a button: on mobile this is a page change, and a
          driver should be able to middle-click, Cmd-click or copy it. The
          stretched ::after overlay makes the whole card — title, blurb, chips
          AND pay — the target, which is what the mockup specifies, without
          nesting Apply inside it. */}
      <Link
        to={selectHref}
        aria-current={isSelected ? 'true' : undefined}
        onClick={(event) => {
          // Let the browser handle new-tab/new-window intents natively.
          if (
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0
          ) {
            return
          }
          event.preventDefault()
          onSelect(card)
        }}
        className="text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-doubleNickel-brand-500 focus-visible:ring-offset-2"
      >
        <span className="block text-[15px] font-bold leading-[1.4] text-doubleNickel-gray-900">
          {card.title}
        </span>
        {/* Omitted entirely when null — never filled with `description` */}
        {card.subtitle && (
          <span className="mt-1.5 line-clamp-2 block text-[13px] leading-[1.6] text-doubleNickel-gray-600">
            {card.subtitle}
          </span>
        )}
      </Link>

      {displayChips.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {displayChips.map((chip) => (
            <Chip key={chip}>{chip}</Chip>
          ))}
        </div>
      )}

      {/* mt-auto pins the row to the card's bottom so Apply lines up across a
          row of cards regardless of blurb length */}
      <div
        className={cn(
          'mt-auto flex items-center pt-3.5',
          showApply && 'justify-between gap-3'
        )}
      >
        {/* No pay → no pay line at all, and no placeholder */}
        {pay && (
          <span className="text-[16px] font-extrabold tracking-[-0.01em] text-doubleNickel-gray-900">
            {pay}
          </span>
        )}
        {/* relative + z-10 lifts Apply above the stretched overlay so it stays
            a separate control rather than being swallowed by the card link */}
        {showApply && (
          <Link
            to={selectHref}
            aria-label={`Apply for ${card.title}`}
            onClick={(event) => {
              if (
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey ||
                event.button !== 0
              ) {
                return
              }
              event.preventDefault()
              onSelect(card)
            }}
            className="relative z-10 ml-auto inline-flex h-9 flex-none items-center justify-center whitespace-nowrap rounded-lg bg-doubleNickel-brand-500 px-4 text-[13px] font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-doubleNickel-brand-500 focus-visible:ring-offset-2"
          >
            Apply
          </Link>
        )}
      </div>
    </article>
  )
}

export default JobCard
